import { useTranslations } from 'next-intl';
import { useEffect, useRef } from 'react';
import type { MessageKey } from 'types/LocaleMessageKey';

export interface MetadataConfig {
  title: string;
  description: string;
}

const updateOrCreateMetaTag = ({
  content,
  identifierAttribute,
  identifierValue,
}: {
  content: string;
  identifierAttribute: string;
  identifierValue: string;
}) => {
  const metaTag = document.querySelector(
    `meta[${identifierAttribute}="${identifierValue}"]`,
  );

  // If meta tag already exists, update its content. Otherwise, create the meta tag
  if (metaTag) {
    metaTag.setAttribute('content', content);
  } else {
    const newMetaTag = document.createElement('meta');
    newMetaTag.setAttribute(identifierAttribute, identifierValue);
    newMetaTag.setAttribute('content', content);
    document.head.appendChild(newMetaTag);
  }
};

/**
 * Custom hook for dynamically updating document metadata for ring finishes.
 * This hook handles getting SEO metadata for a given finish and updating document title
 * and meta tags for description, og:title, and og:description.
 *
 * @param description - The description message key
 * @param title - The title message key
 */
const useDynamicSeoMetadata = ({
  description,
  title,
}: {
  description: MessageKey;
  title: MessageKey;
}) => {
  const t = useTranslations();

  // Store references to previous metadata values to prevent unnecessary updates
  const previousDescription = useRef<MessageKey | null>(null);
  const previousTitle = useRef<MessageKey | null>(null);

  useEffect(() => {
    // If title has changed, update document title and og:title
    if (title !== previousTitle.current) {
      const translatedTitle = t(title);

      // Update document title to new value
      document.title = translatedTitle;

      // Update or create og:title meta tag
      updateOrCreateMetaTag({
        content: translatedTitle,
        identifierAttribute: 'property',
        identifierValue: 'og:title',
      });

      // Update ref to new value
      previousTitle.current = title;
    }

    // If description has changed, update document description and og:description
    if (description !== previousDescription.current) {
      const translatedDescription = t(description);

      // Update or create meta description
      updateOrCreateMetaTag({
        content: translatedDescription,
        identifierAttribute: 'name',
        identifierValue: 'description',
      });

      // Update or create og:description
      updateOrCreateMetaTag({
        content: translatedDescription,
        identifierAttribute: 'property',
        identifierValue: 'og:description',
      });

      previousDescription.current = description;
    }
  }, [description, title, t]);
};

export default useDynamicSeoMetadata;
